import "@/App.css";
import "leaflet/dist/leaflet.css";
import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { isAdminUser } from "@/lib/roles";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import DashboardLayout from "@/pages/DashboardLayout";
import AdminDashboard from "@/pages/AdminDashboard";
import ClientsList from "@/pages/ClientsList";
import ClientDetail from "@/pages/ClientDetail";
import InventoryPage from "@/pages/InventoryPage";
import VendorsPage from "@/pages/VendorsPage";
import EndpointPartnersPage from "@/pages/EndpointPartnersPage";
import FieldMap from "@/pages/FieldMap";
import CalendarPage from "@/pages/CalendarPage";
import RemindersPage from "@/pages/RemindersPage";
import AnalyticsPage from "@/pages/AnalyticsPage";
import OrdersPage from "@/pages/OrdersPage";
import WholesaleOrder from "@/pages/WholesaleOrder";
import RetailSubPortal from "@/pages/RetailSubPortal";
import RetailPriceManager from "@/pages/RetailPriceManager";
import ChatPage from "@/pages/ChatPage";
import SupplyChainPage from "@/pages/SupplyChainPage";
import PurchaseOrdersPage from "@/pages/PurchaseOrdersPage";
import ReconcilePage from "@/pages/ReconcilePage";
import ReconciliationPage from "@/pages/ReconciliationPage";
import ShopPage from "@/pages/ShopPage";
import PublicClientApply from "@/pages/PublicClientApply";
import PublicEpApply from "@/pages/PublicEpApply";
import EpApplicationsPage from "@/pages/EpApplicationsPage";
import PersonnelApply from "@/pages/PersonnelApply";
import EssencePage from "@/pages/EssencePage";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import TeamPage from "@/pages/TeamPage";
import StripeEventsPage from "@/pages/StripeEventsPage";
import DiscountsPage from "@/pages/DiscountsPage";
import ProfilePage from "@/pages/ProfilePage";
import LicenseManagement from "@/pages/LicenseManagement";
import RecycleBinPage from "@/pages/RecycleBinPage";
import MPressChatWidget from "@/components/MPressChatWidget";
import CartCountdownBadge from "@/components/CartCountdownBadge";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-gray-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  // Pending users are bounced back to login with the awaiting-approval banner.
  if (user?.status === "pending") return <Navigate to="/login?pending=1" replace />;
  return children;
}

function AdminOnly({ children }) {
  const { user } = useAuth();
  if (!isAdminUser(user)) return <Navigate to="/" replace />;
  return children;
}

// Detect Emergent Auth callback hash synchronously (BEFORE routing) to avoid race conditions.
function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/order" element={<WholesaleOrder />} />
      <Route path="/shop" element={<ShopPage />} />
      <Route path="/apply" element={<PublicClientApply />} />
      <Route path="/apply-ep" element={<PublicEpApply />} />
      <Route path="/join" element={<PersonnelApply />} />
      <Route path="/essence" element={<EssencePage />} />
      <Route path="/shop/:slug" element={<RetailSubPortal />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/" element={<Protected><DashboardLayout /></Protected>}>
        <Route index element={<AdminDashboard />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="clients" element={<ClientsList />} />
        <Route path="clients/:id" element={<ClientDetail />} />
        <Route path="orders" element={<OrdersPage />} />
        <Route path="inventory" element={<InventoryPage />} />
        <Route path="vendors" element={<VendorsPage />} />
        <Route path="partners" element={<EndpointPartnersPage />} />
        <Route path="ep-applications" element={<AdminOnly><EpApplicationsPage /></AdminOnly>} />
        <Route path="field" element={<FieldMap />} />
        <Route path="calendar" element={<CalendarPage />} />
        <Route path="reminders" element={<RemindersPage />} />
        <Route path="analytics" element={<AnalyticsPage />} />
        <Route path="retail/:clientId/manage" element={<RetailPriceManager />} />
        <Route path="chat" element={<ChatPage />} />
        <Route path="supply-chain" element={<SupplyChainPage />} />
        <Route path="purchase-orders" element={<PurchaseOrdersPage />} />
        <Route path="reconcile" element={<ReconcilePage />} />
        <Route path="reconciliation" element={<ReconciliationPage />} />
        <Route path="team" element={<AdminOnly><TeamPage /></AdminOnly>} />
        <Route path="stripe-events" element={<AdminOnly><StripeEventsPage /></AdminOnly>} />
        <Route path="discounts" element={<AdminOnly><DiscountsPage /></AdminOnly>} />
        <Route path="licenses" element={<AdminOnly><LicenseManagement /></AdminOnly>} />
        <Route path="recycle-bin" element={<Protected><RecycleBinPage /></Protected>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="bottom-left" richColors />
        <AppRouter />
        <MPressChatWidget />
        <CartCountdownBadge />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
